import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Container,
  CircularProgress,
  styled,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import useAdd from "../api/useAdd";
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/moiclogo.png';

// Styled RTL TextField component with theme-aware colors
const RTLTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'error',
})(({ theme, error }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    direction: 'rtl',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInputBase-input': {
    textAlign: 'right',
    direction: 'rtl',
    padding: '16px 14px',
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    right: 16,
    left: 'auto',
    transformOrigin: 'top right',
    textAlign: 'right',
    paddingRight: 6,
    paddingLeft: 6,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : 'white',
    borderRadius: 4,
    zIndex: 1,
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(-14px, -9px) scale(0.75)',
    right: 16,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : 'white',
    paddingRight: 6,
    paddingLeft: 6,
    borderRadius: 4,
    zIndex: 1,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    textAlign: 'right',
    paddingRight: 8,
    borderColor: error ? theme.palette.error.main : theme.palette.divider,
  },
  '& .MuiFormHelperText-root': {
    textAlign: 'right',
    color: theme.palette.text.secondary,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 8,
    marginRight: 0,
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 12,
  },
}));

// RTL form control label 
const RTLFormControlLabel = styled(FormControlLabel)({
  '& .MuiFormControlLabel-label': {
    fontWeight: 400,
    direction: 'rtl',
    textAlign: 'right',
  },
});

// Helper function to get current year
const getCurrentYear = () => new Date().getFullYear();

const Login = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['auth', 'common', 'app']);
  const [showPassword, setShowPassword] = useState(false);
  const [language, setLanguage] = useState(i18n.language || 'dari');
  const [error, setError] = useState(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const { handleAdd, loading, success, responseData } = useAdd("users/login", null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  // Set initial language if needed
  useEffect(() => {
    if (i18n.language !== 'dari' && i18n.language !== 'ps') {
      i18n.changeLanguage('dari');
      setLanguage('dari');
    }
    
    // Ensure RTL direction is set
    document.dir = "rtl";
    document.body.style.direction = "rtl";
    document.body.style.textAlign = "right";
  }, [i18n]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleLanguageChange = (event) => {
    const lang = event.target.value;
    setLanguage(lang);
    i18n.changeLanguage(lang);

    // Both languages are RTL
    document.dir = "rtl";
    document.body.style.direction = "rtl";
    document.body.style.textAlign = "right";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Check if already logged in
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate("/dashboard");
    }
  }, [auth.isAuthenticated, navigate]);

  // Handle successful login
  useEffect(() => {
    if (success && responseData) {
      try {
        auth.login(responseData);
        setTimeout(() => navigate("/dashboard"), 500);
      } catch (error) {
        console.error('Login error:', error);
        setError(t('auth:loginFailed'));
      }
    }
  }, [success, responseData, auth, navigate, t]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.password) {
      setError(t('auth:fieldsRequired'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('auth:invalidEmail'));
      return;
    }

    handleAdd(formData);
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: theme.spacing(2),
      })}
    >
      <Container maxWidth="sm" sx={{ width: '100%' }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            width: '100%',
            maxWidth: '500px',
            mx: 'auto',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Top accent border */}
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            })}
          />

          {/* Language selector */}
          <Box
            sx={{
              alignSelf: 'flex-start',
              mb: 3,
            }}
          >
            <FormControl size="small" variant="outlined">
              <Select
                value={language}
                onChange={handleLanguageChange}
                sx={{
                  borderRadius: 4,
                  fontSize: '0.875rem',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                  textAlign: 'right',
                  direction: 'rtl',
                  '& .MuiSelect-select': {
                    paddingRight: 32,
                    paddingLeft: 8,
                  },
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <LanguageIcon fontSize="small" color="action" />
                  </InputAdornment>
                }
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                }}
              >
                <MenuItem value="dari">{t('common:dari')}</MenuItem>
                <MenuItem value="ps">{t('common:pashto')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Header with logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img 
              src={logo} 
              alt={t('app:ministry_full_name')} 
              style={{ width: 90, height: 90, objectFit: 'contain', marginBottom: 12 }}
            />
            <Typography 
              variant="h6" 
              color="textPrimary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {t('app:ministry_full_name')}
            </Typography>
            <Typography 
              variant="body1" 
              color="textSecondary"
              sx={{ fontWeight: 500 }}
            >
              {t('app:system_full_name')}
            </Typography>
          </Box>

          {/* Login title */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
          >
            {t('auth:login')}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 3 }}
          >
            {t('auth:signInText')}
          </Typography>

          {/* Error alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, width: '100%', borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <RTLTextField
              required
              fullWidth
              id="email"
              label={t('auth:email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              sx={{ mb: 2 }}
              type="email"
              error={!!error && error.includes('email')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ width: 24 }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <RTLTextField
              required
              fullWidth
              name="password"
              label={t('auth:password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              sx={{ mb: 2 }}
              error={!!error && error.includes('password')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
              }}
            >
              <RTLFormControlLabel
                control={
                  <Checkbox
                    name="rememberMe"
                    color="primary"
                    checked={formData.rememberMe}
                    onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('auth:rememberMe')}</Typography>}
              />
              
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', fontWeight: 500 }}
              >
                {t('auth:forgotPassword')}
              </Typography>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(63, 81, 181, 0.25)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(63, 81, 181, 0.35)',
                  backgroundColor: theme.palette.primary.dark,
                },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth:login')
              )}
            </Button>
          </Box>
          
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 3, textAlign: 'center' }}
          >
            {t('auth:loginHelp')}
          </Typography>
        </Paper>

        {/* Copyright Footer */}
        <Box
          sx={{
            textAlign: 'center',
            py: 2,
          }}
        >
          <Divider sx={{ mb: 2, opacity: 0.3 }} />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontSize: '0.875rem',
              opacity: 0.8,
              fontWeight: 400,
            }}
          >
            {t('app:copyright', { year: getCurrentYear() })}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              fontSize: '0.75rem',
              opacity: 0.6,
              display: 'block',
              mt: 0.5,
            }}
          >
            {t('app:all_rights_reserved')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
